<template>
  <div>
    <v-card max-width="1200" data-v-step="11" class="mx-auto" outlined>
      <v-container>
        <v-form
          ref="generalParametersForm"
          v-model="validGeneralParametersForm"
          lazy-validation
          @submit.prevent="onSubmit"
        >
          <v-row justify="space-between" align="center">
            <v-col cols="6" xl="auto" class="order-0">
              <v-row align="baseline" class="ml-1">
                <v-icon class="mr-1">mdi-cog-play-outline</v-icon>
                <span class="headline">{{
                  $t('parameterization.generals.title')
                }}</span>
              </v-row>
            </v-col>
          </v-row>
          <v-row justify="space-between" align="center">
            <v-col cols="12" md="5" lg="5"> </v-col>
          </v-row>
          <v-row align="center">
            <v-col cols="3">
              <v-text-field
                :disabled="loading"
                :label="$t('parameterization.generals.inactivityLimit')"
                prepend-icon="mdi-clock-outline"
                :value="inactivityLimit"
                @input="inactivityLimit = $event"
                :rules="inactivityLimitRules"
                dense
                autocomplete="false"
                type="number"
                id="inactivityLimit-input"
              />
            </v-col>
            <!-- <v-col cols="1"></v-col> -->
            <v-col cols="3">
              <v-text-field
                :disabled="loading"
                :label="$t('parameterization.generals.loadLastDays')"
                prepend-icon="mdi-clock-outline"
                :value="loadLastDays"
                @input="loadLastDays = $event"
                :rules="loadLastDaysRules"
                dense
                autocomplete="false"
                type="number"
                id="loadLastDays-input"
              />
            </v-col>
            <!-- <v-col cols="1"></v-col> -->
            <v-col cols="4" v-if="hasExternalsAuths">
              <v-switch
                v-model="requiredExternalAuthSwitch"
                @change="changeRequiredExternalAuth"
                :label="$t('externalAuth.required')"
                :disabled="externalsAuths.length === 0"
                class="body-1 text-end pa-0 ma-0"
                id="external-auth"
              >
              </v-switch>
            </v-col>
          </v-row>

          <v-row align="center">
            <v-col cols="3">
              <v-text-field
                :disabled="loading"
                :label="$t('parameterization.generals.maxOperatorsPreview')"
                :hint="$t('parameterization.generals.maxOperatorsPreviewHint')"
                persistent-hint
                prepend-icon="mdi-account-group"
                :value="maxOperatorsPreview"
                @input="maxOperatorsPreview = $event"
                :rules="maxOperatorsPreviewRules"
                dense
                autocomplete="false"
                type="number"
                id="maxOperatorsPreview-input"
              />
            </v-col>

            <v-col cols="2">
              <v-btn
                :disabled="loading || areSameValues"
                :loading="loading"
                class="primary secondary--text"
                @click="updateGenerals()"
              >
                {{ $t('parameterization.generals.update') }}
                <template v-slot:loader>
                  <v-progress-circular indeterminate size="10" width="2" />
                  <span class="ml-2">{{
                    $t('parameterization.generals.updating')
                  }}</span>
                </template>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <v-card-actions class="headline justify-end" style="max-height: 50px">
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { getUserInfo, updateUserInfo } from '@/services/user-service'
import rules from '@/utils/rules'
import { mapGetters, mapMutations } from 'vuex'
import getErrorText from '@/utils/get-error-text'

export default {
  props: {
    externalsAuths: Array,
  },
  components: {},
  async beforeMount() {
    this.parentUserId = this.$store.state.user.parentRef
      ? this.$store.state.user.parentRef.id
      : this.$store.state.user.id
    this.parentUser = await getUserInfo(this.parentUserId)
    this.inactivityLimit =
      this.parentUser?.securitySettings?.inactivityLimit ?? 0
    this.loadLastDays = this.parentUser?.clientConfiguration?.loadLastDays ?? 90
    this.maxOperatorsPreview =
      this.parentUser?.clientConfiguration?.maxOperatorsPreview ?? 8
    this.requiredExternalAuth = this.parentUser?.externalAuth?.required ?? false
    this.requiredExternalAuthSwitch = this.requiredExternalAuth
  },
  data() {
    return {
      validGeneralParametersForm: false,
      loading: false,
      requiredExternalAuth: false,
      requiredExternalAuthSwitch: false,
      inactivityLimit: null,
      loadLastDays: null,
      maxOperatorsPreview: null,
      parentUserId: null,
      parentUser: {},
      inactivityLimitRules: [
        rules.notEmptyNumber,
        rules.maxNumberValue(43800),
        rules.minNumberValue(0),
      ],
      loadLastDaysRules: [
        rules.notEmptyNumber,
        rules.maxNumberValue(120),
        rules.minNumberValue(15),
      ],
      maxOperatorsPreviewRules: [
        rules.notEmptyNumber,
        rules.maxNumberValue(25),
        rules.minNumberValue(1),
      ],
    }
  },
  methods: {
    ...mapMutations(['setSnackbar']),
    ...rules,
    changeRequiredExternalAuth() {},
    async updateGenerals() {
      if (!this.$refs.generalParametersForm?.validate()) return
      if (this.areSameValues) return

      this.loading = true
      try {
        this.parentUser.externalAuth = this.parentUser.externalAuth ?? {}
        if (this.hasExternalsAuths) {
          this.parentUser.externalAuth.required =
            this.requiredExternalAuthSwitch
          this.requiredExternalAuth = this.requiredExternalAuthSwitch
        }
        this.parentUser.securitySettings =
          this.parentUser.securitySettings ?? {}
        this.parentUser.securitySettings.inactivityLimit = Number(
          this.inactivityLimit
        )
        this.parentUser.clientConfiguration =
          this.parentUser.clientConfiguration ?? {}
        this.parentUser.clientConfiguration.loadLastDays = Number(
          this.loadLastDays
        )
        this.parentUser.clientConfiguration.maxOperatorsPreview = Number(
          this.maxOperatorsPreview
        )
        await updateUserInfo(this.parentUserId, {
          externalAuth: this.parentUser.externalAuth,
          securitySettings: this.parentUser.securitySettings,
          clientConfiguration: this.parentUser.clientConfiguration,
        })
        const message = this.$t('parameterization.generals.success.update')
        this.setSnackbar({ position: 'top', type: 'success', message })
      } catch (err) {
        const message = getErrorText(err.message)
        this.setSnackbar({ position: 'top', type: 'error', message })
      }
      this.loading = false
    },
  },
  computed: {
    ...mapGetters(['isMobile', 'isAdminUser', 'hasExternalsAuths']),
    areSameValues() {
      return (
        this.requiredExternalAuthSwitch === this.requiredExternalAuth &&
        Number(this.inactivityLimit) ===
          this.parentUser?.securitySettings?.inactivityLimit &&
        Number(this.loadLastDays) ===
          this.parentUser?.clientConfiguration?.loadLastDays &&
        Number(this.maxOperatorsPreview) ===
          this.parentUser?.clientConfiguration?.maxOperatorsPreview
      )
    },
  },
}
</script>
<style lang="scss" scoped></style>
